import React, { useState, useEffect } from "react";
import { supabase, ContactSubmission, CustomerDomain, Profile, ProductOrder, ProjectIntake } from "../lib/supabase";
import {
  buildIntakePrompterMarkdown,
  buildSimpleHandoffPrompt,
  intakeBriefParamsFromStoredOrder,
} from "../lib/buildIntakeBrief";
import { fallbackCatalogItems, formatRand } from "../lib/productPlans";

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [productOrders, setProductOrders] = useState<ProductOrder[]>([]);
  const [projectIntakes, setProjectIntakes] = useState<ProjectIntake[]>([]);
  const [customerDomains, setCustomerDomains] = useState<CustomerDomain[]>([]);
  const [domainNotes, setDomainNotes] = useState<Record<string, string>>({});
  const [domainActionMessage, setDomainActionMessage] = useState<Record<string, string>>({});
  const [showModal, setShowModal] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread' | 'read' | 'replied'>('all');

  const isPaidProductOrder = (status: ProductOrder['status']) =>
    status === 'paid' || status === 'active' || status === 'in_progress' || status === 'completed';

  const flashCopied = (key: string) => {
    setCopiedField(key);
    window.setTimeout(() => {
      setCopiedField((current) => (current === key ? null : current));
    }, 2000);
  };

  const copyOrderHandoff = (order: ProductOrder) => {
    const params = intakeBriefParamsFromStoredOrder(order, fallbackCatalogItems);
    if (!params) return;
    void navigator.clipboard.writeText(buildSimpleHandoffPrompt(params));
    flashCopied(`handoff-${order.id}`);
  };

  const copyOrderBriefMarkdown = (order: ProductOrder) => {
    const params = intakeBriefParamsFromStoredOrder(order, fallbackCatalogItems);
    if (!params) return;
    void navigator.clipboard.writeText(buildIntakePrompterMarkdown(params));
    flashCopied(`markdown-${order.id}`);
  };

  const downloadOrderBriefMarkdown = (order: ProductOrder) => {
    const params = intakeBriefParamsFromStoredOrder(order, fallbackCatalogItems);
    if (!params) return;
    const md = buildIntakePrompterMarkdown(params);
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `designdotdev-brief-${order.id.slice(0, 8)}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
      if (session) {
        const userProfile = await fetchProfile(session.user.id);
        if (userProfile?.role === 'admin') {
          fetchSubmissions();
          fetchProductOrders();
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    setProfile(data);
    return data as Profile;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginData.email,
        password: loginData.password,
      });

      if (error) throw error;

      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(true);
      if (session) {
        const userProfile = await fetchProfile(session.user.id);
        if (userProfile.role === 'admin') {
          fetchSubmissions();
          fetchProductOrders();
        } else {
          setLoginError('This account is not an admin account.');
          await supabase.auth.signOut();
          setIsAuthenticated(false);
          setProfile(null);
        }
      }
    } catch (error: any) {
      setLoginError(error.message || 'Login failed');
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setProfile(null);
    setSubmissions([]);
  };

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchProductOrders = async () => {
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('product_orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      const orders = (ordersData || []) as ProductOrder[];
      setProductOrders(orders);
      setDomainNotes(
        orders.reduce<Record<string, string>>((acc, order) => {
          acc[order.id] = order.domain_notes || '';
          return acc;
        }, {}),
      );

      if (orders.length) {
        const orderIds = orders.map((order) => order.id);
        const [intakeResult, domainResult] = await Promise.all([
          supabase
            .from('project_intakes')
            .select('*')
            .in('order_id', orderIds),
          supabase
            .from('customer_domains')
            .select('*')
            .in('order_id', orderIds),
        ]);

        if (intakeResult.error) throw intakeResult.error;
        if (domainResult.error) throw domainResult.error;
        setProjectIntakes((intakeResult.data || []) as ProjectIntake[]);
        setCustomerDomains((domainResult.data || []) as CustomerDomain[]);
      } else {
        setProjectIntakes([]);
        setCustomerDomains([]);
      }
    } catch (error) {
      console.error('Error fetching product orders:', error);
    }
  };

  const registerCustomerDomain = async (customerDomain: CustomerDomain) => {
    setDomainActionMessage((current) => ({ ...current, [customerDomain.id]: 'Registering domain with Domains.co.za...' }));

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) throw new Error('Please log in again.');

      const response = await fetch('/api/domain-register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ customerDomainId: customerDomain.id }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Domain registration failed.');

      setDomainActionMessage((current) => ({ ...current, [customerDomain.id]: 'Domain registered successfully.' }));
      await fetchProductOrders();
    } catch (error: any) {
      setDomainActionMessage((current) => ({
        ...current,
        [customerDomain.id]: error.message || 'Domain registration failed.',
      }));
    }
  };

  const updateDomainStatus = async (order: ProductOrder, purchased: boolean) => {
    const purchasedAt = purchased ? new Date().toISOString() : null;
    const domainNote = domainNotes[order.id] || '';

    try {
      const { error: orderError } = await supabase
        .from('product_orders')
        .update({
          domain_purchased: purchased,
          domain_purchased_at: purchasedAt,
          domain_notes: domainNote,
        })
        .eq('id', order.id);

      if (orderError) throw orderError;

      const intake = projectIntakes.find((item) => item.order_id === order.id);
      if (intake) {
        const { error: intakeError } = await supabase
          .from('project_intakes')
          .update({
            domain_purchased: purchased,
            domain_purchased_at: purchasedAt,
            domain_notes: domainNote,
          })
          .eq('id', intake.id);

        if (intakeError) throw intakeError;
      }

      setProductOrders((current) =>
        current.map((item) =>
          item.id === order.id
            ? { ...item, domain_purchased: purchased, domain_purchased_at: purchasedAt, domain_notes: domainNote }
            : item,
        ),
      );
      setProjectIntakes((current) =>
        current.map((item) =>
          item.order_id === order.id
            ? { ...item, domain_purchased: purchased, domain_purchased_at: purchasedAt, domain_notes: domainNote }
            : item,
        ),
      );
    } catch (error) {
      console.error('Error updating domain status:', error);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contact_submissions')
        .update({ read: true })
        .eq('id', id);

      if (error) throw error;
      
      // Update local state
      setSubmissions(prev => 
        prev.map(sub => 
          sub.id === id ? { ...sub, read: true } : sub
        )
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAsReplied = async (id: string) => {
    try {
      const { data: sessionRes } = await supabase.auth.getSession();
      const currentEmail = sessionRes?.session?.user?.email || '';

      const { error } = await supabase
        .from('contact_submissions')
        .update({ replied: true, replied_by: currentEmail, replied_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      setSubmissions(prev =>
        prev.map(sub =>
          sub.id === id ? { ...sub, replied: true, replied_by: currentEmail, replied_at: new Date().toISOString() } : sub
        )
      );
    } catch (error) {
      console.error('Error marking as replied:', error);
    }
  };

  const handleRowClick = (submission: ContactSubmission) => {
    setSelectedSubmission(submission);
    setShowModal(true);
    // Mark as read when opened
    if (!submission.read) {
      markAsRead(submission.id);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSubmission(null);
    setCopiedField(null);
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Filter submissions based on active filter
  const filteredSubmissions = submissions.filter(submission => {
    switch (activeFilter) {
      case 'unread':
        return !submission.read;
      case 'read':
        return submission.read;
      case 'replied':
        return submission.replied;
      default:
        return true; // 'all'
    }
  });

  // Stats calculations
  const stats = {
    total: submissions.length,
    unread: submissions.filter(s => !s.read).length,
    read: submissions.filter(s => s.read).length,
    replied: submissions.filter(s => s.replied).length,
  };

  // Copy Button Component
  const CopyButton = ({ text, fieldName }: { text: string; fieldName: string }) => {
    const isCopied = copiedField === fieldName;
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          copyToClipboard(text, fieldName);
        }}
        className={`ml-2 px-2 py-1 text-xs rounded transition-all duration-200 ${
          isCopied 
            ? 'bg-green-500 text-white' 
            : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200'
        }`}
        title={isCopied ? 'Copied!' : 'Copy to clipboard'}
      >
        {isCopied ? '✓' : '📋'}
      </button>
    );
  };

  // Stats Card Component
  const StatsCard = ({ 
    title, 
    count, 
    filter, 
    isActive, 
    onClick 
  }: { 
    title: string; 
    count: number; 
    filter: 'all' | 'unread' | 'read' | 'replied'; 
    isActive: boolean; 
    onClick: () => void; 
  }) => {
    return (
      <button
        onClick={onClick}
        className={`p-6 rounded-lg border transition-all duration-200 ${
          isActive 
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' 
            : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
        style={{ borderColor: isActive ? 'var(--accent-primary)' : 'var(--border-primary)' }}
      >
        <div className={`text-2xl font-bold ${isActive ? 'text-blue-600 dark:text-blue-400' : ''}`} style={{ color: isActive ? 'var(--accent-primary)' : 'var(--text-primary)' }}>
          {count}
        </div>
        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          {title}
        </div>
      </button>
    );
  };

  // Modal Component
  const SubmissionModal = ({ submission, onClose }: { submission: ContactSubmission; onClose: () => void }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b" style={{ borderColor: 'var(--border-primary)' }}>
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Contact Submission Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status Badges */}
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  submission.read 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                }`}>
                  {submission.read ? 'Read' : 'New'}
                </span>
                {submission.replied && (
                  <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                    Replied{submission.replied_by ? ` by ${submission.replied_by.split('@')[0]}` : ''}
                  </span>
                )}
              </div>
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {formatDate(submission.created_at)}
              </span>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Name
                </label>
                <div className="flex items-center">
                  <div className="flex-1 p-3 rounded-md" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                    {submission.name}
                  </div>
                  <CopyButton text={submission.name} fieldName="name" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Email
                </label>
                <div className="flex items-center">
                  <div className="flex-1 p-3 rounded-md" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                    <a href={`mailto:${submission.email}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                      {submission.email}
                    </a>
                  </div>
                  <CopyButton text={submission.email} fieldName="email" />
                </div>
              </div>
            </div>

            {/* Service */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Service Requested
              </label>
              <div className="flex items-center">
                <div className="flex-1 p-3 rounded-md" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                  {submission.service}
                </div>
                <CopyButton text={submission.service} fieldName="service" />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Message
              </label>
              <div className="flex items-start">
                <div className="flex-1 p-4 rounded-md whitespace-pre-wrap" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', minHeight: '120px' }}>
                  {submission.message}
                </div>
                <CopyButton text={submission.message} fieldName="message" />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t" style={{ borderColor: 'var(--border-primary)' }}>
              <button
                onClick={() => {
                  window.open(`mailto:${submission.email}?subject=Re: ${submission.service} Inquiry`, '_blank');
                  markAsReplied(submission.id);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              >
                Reply via Email
              </button>
              {!submission.replied && (
                <button
                  onClick={() => markAsReplied(submission.id)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
                >
                  Mark as Replied
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 border rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-xl" style={{ color: 'var(--text-primary)' }}>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 border" style={{ borderColor: 'var(--border-primary)' }}>
            <h1 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
              Admin Login
            </h1>
            
            {loginError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {loginError}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors"
                  style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors"
                  style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                />
              </div>

              <button
                type="submit"
                className="w-full px-4 py-2 font-medium rounded-md hover:opacity-90 transition"
                style={{ background: 'linear-gradient(to right, var(--accent-primary), var(--accent-secondary))', color: 'var(--text-primary)' }}
              >
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (profile?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 border text-center" style={{ borderColor: 'var(--border-primary)' }}>
          <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            Admin access required
          </h1>
          <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
            This account is signed in, but it does not have admin permissions.
          </p>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em]" style={{ color: 'var(--accent-primary)' }}>
              Admin command
            </p>
            <h1 className="mt-2 text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Client Operations
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
          >
            Logout
          </button>
        </div>

        <div className="mb-8 rounded-lg border bg-white p-5 dark:bg-gray-800" style={{ borderColor: 'var(--border-primary)' }}>
          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Customer Website Profiles
              </h2>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Manage real customer website orders, domain registration status, and domain notes.
              </p>
            </div>
            <button
              type="button"
              onClick={fetchProductOrders}
              className="rounded-md border px-4 py-2 text-sm font-bold"
              style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
            >
              Refresh
            </button>
          </div>

          {productOrders.length === 0 ? (
            <p className="rounded-md border p-4 text-sm" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}>
              No product orders yet.
            </p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {productOrders.map((order) => {
                const intake = projectIntakes.find((item) => item.order_id === order.id);
                const customerDomain = customerDomains.find((item) => item.order_id === order.id);
                const email = (order.metadata?.email as string | undefined) || order.user_id;
                const desiredDomain = customerDomain?.full_domain || order.desired_domain || intake?.desired_domain || (order.metadata?.spec as any)?.domain?.desiredDomain || '';
                const needsDomain = Boolean(order.domain_needs_registration || intake?.domain_needs_registration || (order.metadata?.spec as any)?.domain?.needsRegistration);
                const purchased = Boolean(order.domain_purchased || intake?.domain_purchased);
                const domainStatus = customerDomain?.status || (purchased ? 'registered' : needsDomain ? 'requested' : 'none');
                const orderBriefParams = intakeBriefParamsFromStoredOrder(order, fallbackCatalogItems);

                return (
                  <div key={order.id} className="rounded-lg border p-4" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-secondary)' }}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent-primary)' }}>
                          {order.tier} / {order.billing_mode}
                        </p>
                        <h3 className="mt-1 text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{email}</h3>
                        <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Order {order.id.slice(0, 8)} · {formatDate(order.created_at)}
                        </p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${purchased || domainStatus === 'registered' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200'}`}>
                        {domainStatus === 'none' ? 'No domain request' : domainStatus.replace(/_/g, ' ')}
                      </span>
                    </div>

                    <div className="mt-4 rounded-md border p-3" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--card-bg)' }}>
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Domain</p>
                      <p className="mt-1 font-bold" style={{ color: 'var(--text-primary)' }}>
                        {desiredDomain || (needsDomain ? 'Desired domain not chosen yet' : 'Customer already has / no domain request')}
                      </p>
                      {purchased && (order.domain_purchased_at || intake?.domain_purchased_at) ? (
                        <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Purchased {formatDate((order.domain_purchased_at || intake?.domain_purchased_at) as string)}
                        </p>
                      ) : null}
                      {customerDomain ? (
                        <div className="mt-3 grid gap-2 text-xs md:grid-cols-2" style={{ color: 'var(--text-secondary)' }}>
                          <p>Availability: <span className="font-bold">{customerDomain.availability_status}</span></p>
                          <p>Payment: <span className="font-bold">{customerDomain.purchase_paid ? 'paid' : 'awaiting payment'}</span></p>
                          <p>Amount: <span className="font-bold">{formatRand(customerDomain.purchase_amount || 0)}</span></p>
                          <p>Registrar ref: <span className="font-bold">{customerDomain.registrar_reference || 'not registered yet'}</span></p>
                        </div>
                      ) : null}
                    </div>

                    {isPaidProductOrder(order.status) ? (
                      <div className="mt-4 rounded-md border p-3" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--card-bg)' }}>
                        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                          Internal — consult handoff
                        </p>
                        <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                          From the consult snapshot saved when they checked out. Only visible here.
                        </p>
                        {orderBriefParams ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => copyOrderHandoff(order)}
                              className="rounded-md border px-3 py-1.5 text-xs font-bold transition hover:opacity-90"
                              style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                            >
                              {copiedField === `handoff-${order.id}` ? 'Copied build prompt' : 'Copy build prompt'}
                            </button>
                            <button
                              type="button"
                              onClick={() => copyOrderBriefMarkdown(order)}
                              className="rounded-md border px-3 py-1.5 text-xs font-bold transition hover:opacity-90"
                              style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                            >
                              {copiedField === `markdown-${order.id}` ? 'Copied full brief' : 'Copy full brief (markdown)'}
                            </button>
                            <button
                              type="button"
                              onClick={() => downloadOrderBriefMarkdown(order)}
                              className="rounded-md border px-3 py-1.5 text-xs font-bold transition hover:opacity-90"
                              style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                            >
                              Download full brief
                            </button>
                          </div>
                        ) : (
                          <p className="mt-2 text-xs italic" style={{ color: 'var(--text-secondary)' }}>
                            No consult snapshot on this order (older checkouts may predate this capture).
                          </p>
                        )}
                      </div>
                    ) : null}

                    <label className="mt-4 block text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      Admin domain notes
                    </label>
                    <textarea
                      value={domainNotes[order.id] || ''}
                      onChange={(event) => setDomainNotes((current) => ({ ...current, [order.id]: event.target.value }))}
                      rows={3}
                      className="mt-2 w-full rounded-md border px-3 py-2 text-sm"
                      style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
                      placeholder="Registrar, expiry, DNS notes, account used..."
                    />

                    <div className="mt-4 flex flex-wrap gap-2">
                      {customerDomain && customerDomain.purchase_paid && ['registration_ready', 'failed'].includes(customerDomain.status) ? (
                        <button
                          type="button"
                          onClick={() => registerCustomerDomain(customerDomain)}
                          className="rounded-md bg-orange-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-orange-500"
                        >
                          Register with Domains.co.za
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => updateDomainStatus(order, true)}
                        disabled={!needsDomain && !desiredDomain}
                        className="rounded-md bg-green-600 px-4 py-2 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Mark domain purchased
                      </button>
                      <button
                        type="button"
                        onClick={() => updateDomainStatus(order, false)}
                        className="rounded-md border px-4 py-2 text-sm font-bold"
                        style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                      >
                        Mark in progress
                      </button>
                    </div>
                    {customerDomain && domainActionMessage[customerDomain.id] ? (
                      <p className="mt-3 rounded-md border p-3 text-sm" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}>
                        {domainActionMessage[customerDomain.id]}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <h2 className="mb-4 text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Contact Submissions
        </h2>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatsCard
            title="Total Submissions"
            count={stats.total}
            filter="all"
            isActive={activeFilter === 'all'}
            onClick={() => setActiveFilter('all')}
          />
          <StatsCard
            title="Unread"
            count={stats.unread}
            filter="unread"
            isActive={activeFilter === 'unread'}
            onClick={() => setActiveFilter('unread')}
          />
          <StatsCard
            title="Read"
            count={stats.read}
            filter="read"
            isActive={activeFilter === 'read'}
            onClick={() => setActiveFilter('read')}
          />
          <StatsCard
            title="Replied"
            count={stats.replied}
            filter="replied"
            isActive={activeFilter === 'replied'}
            onClick={() => setActiveFilter('replied')}
          />
        </div>

        {/* Filter Info */}
        {activeFilter !== 'all' && (
          <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border" style={{ borderColor: 'var(--border-primary)' }}>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              🔍 Showing {filteredSubmissions.length} {activeFilter} submission{filteredSubmissions.length !== 1 ? 's' : ''}. 
              <button 
                onClick={() => setActiveFilter('all')}
                className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
              >
                Show all
              </button>
            </p>
          </div>
        )}

        {/* Submissions Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-primary)' }}>
          {filteredSubmissions.length === 0 ? (
            <div className="p-8 text-center" style={{ color: 'var(--text-secondary)' }}>
              {activeFilter === 'all' ? 'No submissions yet' : `No ${activeFilter} submissions`}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--border-primary)' }}>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      Service
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      Message Preview
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                  {filteredSubmissions.map((submission) => (
                    <tr 
                      key={submission.id} 
                      className={`${submission.read ? '' : 'bg-blue-50 dark:bg-blue-900/20'} hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors`}
                      onClick={() => handleRowClick(submission)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            submission.read 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          }`}>
                            {submission.read ? 'Read' : 'New'}
                          </span>
                          {submission.replied && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" title={submission.replied_by ? `Replied by ${submission.replied_by}` : 'Replied'}>
                              ✓
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                        {submission.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                        <a 
                          href={`mailto:${submission.email}`} 
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {submission.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                        {submission.service}
                      </td>
                      <td className="px-6 py-4" style={{ color: 'var(--text-primary)' }}>
                        <div className="max-w-xs truncate" title={submission.message}>
                          {submission.message}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {formatDate(submission.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border" style={{ borderColor: 'var(--border-primary)' }}>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            💡 <strong>Tip:</strong> Click on any row to view the full message details in a popup modal. Use the copy buttons (📋) to quickly copy information. Click the stats cards above to filter submissions.
          </p>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedSubmission && (
        <SubmissionModal 
          submission={selectedSubmission} 
          onClose={closeModal} 
        />
      )}
    </div>
  );
};

export default AdminPage;
